/* eslint-disable no-nested-ternary, no-restricted-syntax, func-call-spacing, no-spaced-func */
import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';

// Redux
import { useSelector } from 'react-redux';

// React Virtuoso
import { TableVirtuoso } from 'react-virtuoso/dist/index';

// Material
import Table from '@material-ui/core/Table';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';

// Helpers
import { Order, stableSort, getComparator } from 'helpers/tableSortLabels';
import { formatDate as format } from 'helpers/date';

// Constants
import CONST_TOKEN from 'constants/token';
import { REPORT_GROUPINGS } from 'constants/sellingTerminals';
import CONST_SELLING_POINTS from 'constants/sellingPoints';
import CONST_LINES_OBJECT from 'constants/lines';
import CONST_QRCODE from 'constants/qrcodes';
import CONST_TRANSPORT_VOUCHER_COLLECTION from 'constants/transportVoucherCollectionReport';

// Types
import { ApplicationState } from 'store';
import { TrasactionInterface } from 'store/ducks/employeesSales/types';

// Styles
import useStyle from './transactionNavigation.styles';

interface TransactionNavigationProps {
  setTableReference: (arg: any) => void;
  setTableCSV: (arg: any) => void;
  groupings: Array<string>;
  isHistoryDatails?: boolean;
  transactionsList?: TrasactionInterface[];
  isPrinting?: boolean;
  isMounted?: 'initalState' | 'loading' | 'notLoading';
  setIsMounted?: (param: 'initalState' | 'loading' | 'notLoading') => void;
  setSimplePdfData?: (param: {
    title: string;
    subtitle: string;
    filters: string;
    headers: any;
    rows: any;
  }) => void;
  periodString?: string;
  setTransactionOrder?: (o: Order) => void;
  setTransactionOrderBy?: (o: string) => void;
}

const TransactionNavigation: React.FC<TransactionNavigationProps> = memo(
  ({
    setTableReference,
    setTableCSV,
    groupings,
    isHistoryDatails,
    transactionsList,
    isPrinting,
    isMounted,
    setIsMounted,
    setSimplePdfData,
    periodString,

    setTransactionOrder,
    setTransactionOrderBy,
  }) => {
    const classes = useStyle();
    const employeesList = useSelector(
      (state: ApplicationState) => state.employees.employeesList,
    );

    const linesList = useSelector(
      (state: ApplicationState) => state.lines.linesList,
    );

    const transportOperatorSelected = useSelector(
      (state: ApplicationState) =>
        state.transportOperators.transportOperatorSelected,
    );

    const transportOperatorsList = useSelector(
      (state: ApplicationState) =>
        state.transportOperators.transportOperatorsList,
    );

    // data formatting
    const initialTransactionsList = useSelector((state: ApplicationState) => {
      const list =
        transactionsList && transactionsList.length
          ? transactionsList
          : state.employeesSales.transactionsList;
      if (list.length === 0) return [];
      // if there is no kind of grouping
      if (groupings.length === 0) {
        return list.map((t) => ({
          id: t.id && t.id.split('-')[0],
          amount: t.amount,
          paymentValue: t.paymentValue,
          createdAt: t.createdAt,
          responsible: t.SellingPoint && t.SellingPoint.name,
          tokenStatus: t.Token
            ? CONST_TOKEN.STATUS.find((e) => e.KEY === t.Token.status)?.VALUE
            : '',
          type: t.type
            ? CONST_SELLING_POINTS.TRANSACTION_TYPES.find(
                (e) => e.KEY === t.type,
              )?.VALUE
            : '',
          qrcodeType: t.qrcodeType,
          transportOperatorId: t.transportOperatorId,
          shortId: t.shortId || '---',
        }));
      }
      // if there is any kind of grouping
      return list.map(
        ({
          employeeId,
          count,
          totalValue,
          usedDate,
          type,
          amount,
          paymentValue,
          totalPaymentValue,
          lineSectionId,
          qrcodeType,
          paymentType,
        }) => {
          const ret = {
            count,
            totalValue,
          };
          if (employeeId) {
            Object.assign(ret, ret, { employeeId });
          }
          if (usedDate !== undefined) {
            Object.assign(ret, ret, { usedDate });
          }
          if (type !== undefined) {
            const newType = CONST_SELLING_POINTS.TRANSACTION_TYPES.find(
              (e) => e.KEY === type,
            )?.VALUE;

            Object.assign(ret, ret, {
              type: `${newType}`,
            });
          }

          if (amount !== undefined) {
            const newType = `${amount}`;
            Object.assign(ret, ret, { amount: newType });
          }
          if (paymentValue !== undefined) {
            const newType = `${paymentValue}`;
            Object.assign(ret, ret, { paymentValue: newType });
          }
          if (totalPaymentValue !== undefined) {
            const newType = `${totalPaymentValue}`;
            Object.assign(ret, ret, { totalPaymentValue: newType });
          }
          if (lineSectionId) {
            Object.assign(ret, ret, { lineSectionId });
          }

          if (qrcodeType !== undefined) {
            Object.assign(ret, ret, { qrcodeType });
          }

          if (paymentType !== undefined) {
            Object.assign(ret, ret, { paymentType });
          }
          return ret;
        },
      );
    });

    const [order, setOrder] = useState<Order>('asc');
    const [orderBy, setOrderBy] = useState('amount');
    const [tableColumns, setTableColumns] = useState<
      Array<{ key: string; name: string }>
    >([]);

    // defining table structure
    useEffect(() => {
      if (groupings.length === 0) {
        setTableColumns([
          { key: 'id', name: 'Código da Transação' },
          { key: 'amount', name: 'Valor' },
          { key: 'paymentValue', name: 'Valor Transação' },
          { key: 'qrcodeType', name: 'QRCode' },
          { key: 'createdAt', name: 'Data e Hora' },
          { key: 'shortId', name: 'Identificador' },
        ]);
      } else {
        let columns: Array<{ key: string; name: string }> = [];
        if (groupings.includes(REPORT_GROUPINGS.TRANSACTION.EMPLOYEE)) {
          columns = columns.concat([{ key: 'employeeId', name: 'Motorista' }]);
        }
        if (groupings.includes(REPORT_GROUPINGS.TRANSACTION.USED_DATE)) {
          columns = columns.concat([{ key: 'usedDate', name: 'Data' }]);
        }
        if (groupings.includes(REPORT_GROUPINGS.TRANSACTION.TYPE)) {
          columns = columns.concat([{ key: 'type', name: 'Tipo de Crédito' }]);
        }

        if (groupings.includes(REPORT_GROUPINGS.TRANSACTION.AMOUNT)) {
          columns = columns.concat([
            { key: 'amount', name: 'Valor da Transação' },
          ]);
        }

        if (groupings.includes(REPORT_GROUPINGS.TRANSACTION.LINE_SECTION)) {
          columns = columns.concat([{ key: 'lineSectionId', name: 'Linha' }]);
        }

        columns = columns.concat([
          { key: 'totalValue', name: 'Valor total' },
          { key: 'totalPaymentValue', name: 'Valor Transação' },
          { key: 'count', name: 'Quantidade' },
        ]);

        if (groupings.includes(REPORT_GROUPINGS.TRANSACTION.QRCODE_TYPE)) {
          columns = columns.concat([
            { key: 'qrcodeType', name: 'Tipo de QRCode' },
          ]);
        }

        if (groupings.includes(REPORT_GROUPINGS.TRANSACTION.PAYMENT_TYPE)) {
          columns = columns.concat([
            { key: 'paymentType', name: 'Tipo de Pagamento' },
          ]);
        }

        setTableColumns(columns);
      }
    }, [groupings]);

    const getLineNameByLineSection = (lineSectionId: string): string => {
      for (const line of linesList) {
        if (line.LineSections != null) {
          for (const lineSection of line.LineSections) {
            if (lineSection.id === lineSectionId) {
              return `${line.prefix} - ${line.name} - (${
                lineSection?.name ? lineSection.name : lineSection.Section?.name
              }) ${
                CONST_LINES_OBJECT.WAYS.find((e) => e.KEY === lineSection.way)
                  ?.VALUE
              }`;
            }
          }
        }
      }
      return '';
    };

    // Formatters
    const formatDate = useMemo(
      () => (date: string) => {
        if (parseInt(String(format(date)?.split('/')[0]), 10)) {
          return date && format(date);
        }
        return date;
      },
      [],
    );

    const formatDateTime = useMemo(
      () => (date: Date) => date && new Date(date).toLocaleString('pt-BR'),
      [],
    );

    const formatAmount = useMemo(
      () => (amount: number | string) =>
        amount &&
        `R$ ${amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
      [],
    );

    const formatQrcodeType = useMemo(
      () => (qrcodeType: number) =>
        CONST_QRCODE.ENTRY.find((e) => e.KEY === qrcodeType)?.VALUE || '--',
      [],
    );

    const formatPaymentType = useMemo(
      () => (paymentType: string) =>
        CONST_TRANSPORT_VOUCHER_COLLECTION.PAYMENT_TYPE.find(
          (e) => e.KEY === paymentType,
        )?.VALUE || '--',
      [],
    );

    const formatEmployeer = useMemo(
      () => (employeeId: string) =>
        employeesList.find((employee) => employee.id === employeeId)?.name ||
        '--',
      [employeesList],
    );
    const formatLineSectio = useMemo(
      () => (lineSectionId: string) =>
        getLineNameByLineSection(lineSectionId) || '--',
      [linesList],
    );

    // Field to Formatter Mapping
    const fieldFormatting = new Map<string, (arg: any) => any>([
      ['createdAt', formatDateTime],
      ['usedDate', formatDate],
      ['amount', formatAmount],
      ['paymentValue', formatAmount],
      ['totalPaymentValue', formatAmount],
      ['qrcodeType', formatQrcodeType],
      ['totalValue', formatAmount],
      ['paymentType', formatPaymentType],
      ['employeeId', formatEmployeer],
      ['lineSectionId', formatLineSectio],
    ]);

    const handleRequestSort = useCallback(
      (event: React.MouseEvent<unknown>, property: string) => {
        const isAsc = orderBy === property && order === 'asc';
        const newOrder = isAsc ? 'desc' : 'asc';
        if (setTransactionOrder) setTransactionOrder(newOrder);
        if (setTransactionOrderBy) setTransactionOrderBy(property);
        setOrder(newOrder);
        setOrderBy(property);
      },
      [order, orderBy],
    );
    useEffect(() => {
      if (setIsMounted && isPrinting && isMounted !== 'loading') {
        setIsMounted('loading');
      }
    }, [isPrinting]);
    useEffect(() => {
      if (initialTransactionsList.length > 0) {
        if (
          isPrinting &&
          document.getElementById(
            `tableToPint${initialTransactionsList.length - 1}`,
          )
        ) {
          if (setIsMounted) setIsMounted('notLoading');
        }
      } else if (setIsMounted) setIsMounted('notLoading');
    }, [
      document.getElementById(
        `tableToPint${initialTransactionsList.length - 1}`,
      ),
    ]);

    // generating the table csv
    useEffect(() => {
      let csv = '';

      if (tableColumns.length > 0 && initialTransactionsList.length > 0) {
        csv = tableColumns.reduce((a, c) => `${a}${a ? ';' : ''}${c.name}`, '');
        if (transportOperatorSelected.type === 1) csv += ';Operação';

        let totalValue = 0;
        stableSort(
          stableSort(
            initialTransactionsList,
            getComparator('desc', 'createdAt'),
          ),
          getComparator('asc', 'amount'),
        ).forEach((t: { [x: string]: any }) => {
          if (t.amount) {
            totalValue += parseFloat(t.amount);
          } else if (t.totalValue) {
            totalValue += parseFloat(t.totalValue);
          }

          csv += '\n';

          tableColumns.forEach((c) => {
            const formatter = fieldFormatting.get(c.key);

            if (formatter) {
              csv += `${formatter(t[c.key])};`;
            } else {
              let value = t[c.key];
              if (typeof value === 'number') {
                value = value.toFixed(2).replace('.', ',');
              } else if (!Number.isNaN(value)) {
                value = parseFloat(value)
                  .toFixed(2)
                  .replace('.', ',');
              }
              csv += `${value};`;
            }
          });
          if (transportOperatorSelected.type === 1) {
            csv +=
              transportOperatorsList.find(
                (e) => e.id === t?.transportOperatorId,
              )?.name || '---';
          }
        });

        csv += `\n`;
        csv += `${totalValue.toFixed(2).replace('.', ',')};;;;Total`;
      }

      setTableCSV(csv);
      // eslint-disable-next-line
    }, [tableColumns, initialTransactionsList]);

    useEffect(() => {
      const headerSimplePdf = tableColumns.map((col) => col.name);
      if (transportOperatorSelected.type === 1) {
        headerSimplePdf.push('Operação');
      }
      const rowsTransactionsSimplePdf =
        initialTransactionsList?.length > 0
          ? stableSort(
              initialTransactionsList,
              getComparator(order, orderBy),
            )?.map((transaction: any) => {
              const row = tableColumns.map((col) => {
                const formatter = fieldFormatting.get(col.key);
                if (formatter) {
                  return `${formatter(transaction?.[col.key])}`;
                }
                let value = transaction[col.key];
                if (typeof value === 'number') {
                  value = value.toFixed(2).replace('.', ',');
                } else if (!Number.isNaN(value)) {
                  value = parseFloat(value)
                    .toFixed(2)
                    .replace('.', ',');
                }
                return `${value}`;
              });
              if (transportOperatorSelected.type === 1) {
                row.push(
                  transportOperatorsList.find(
                    (e) => e.id === transaction?.transportOperatorId,
                  )?.name || '---',
                );
              }
              return row;
            })
          : [];

      const tempSimplePdfData = {
        title: 'ONBOARD',
        subtitle: 'Relatório de Vendas à Bordo',
        filters: periodString || '',
        headers: headerSimplePdf,
        rows: rowsTransactionsSimplePdf,
      };

      if (setSimplePdfData) setSimplePdfData(tempSimplePdfData);
    }, [tableColumns, initialTransactionsList]);

    const TableToPrint = () => (
      <TableContainer
        className={classes.tableContainer}
        ref={(ref: any) => setTableReference(ref)}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              {tableColumns.length > 0 &&
                tableColumns.map((col) => (
                  <TableCell
                    className={isHistoryDatails ? classes.firstTd : ''}
                    key={col.key}
                    variant='head'
                    align='center'
                    sortDirection={orderBy === col.key ? order : false}>
                    <TableSortLabel
                      classes={{
                        root: classes.sortLabel,
                        icon: classes.sortLabel,
                      }}
                      active
                      direction={orderBy === col.key ? order : 'asc'}
                      onClick={(event) => handleRequestSort(event, col.key)}>
                      {col.name}
                      {orderBy === col.key ? (
                        <span className={classes.visuallyHidden}>
                          {order === 'desc'
                            ? 'sorted descending'
                            : 'sorted ascending'}
                        </span>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                ))}
              {transportOperatorSelected.type === 1 && (
                <TableCell
                  variant='head'
                  align='left'
                  sortDirection={
                    orderBy === 'transportOperatorId' ? order : false
                  }>
                  <TableSortLabel
                    classes={{
                      root: classes.sortLabel,
                      icon: classes.sortLabel,
                    }}
                    active
                    direction={
                      orderBy === 'transportOperatorId' ? order : 'asc'
                    }
                    onClick={(event) =>
                      handleRequestSort(event, 'transportOperatorId')
                    }>
                    Operação
                    {orderBy === 'transportOperatorId' ? (
                      <span className={classes.visuallyHidden}>
                        {order === 'desc'
                          ? 'sorted descending'
                          : 'sorted ascending'}
                      </span>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {initialTransactionsList.length > 0 &&
              stableSort(
                initialTransactionsList,
                getComparator(order, orderBy),
              ).map((transaction, i: number) => (
                <TableRow key={transaction.id} id={`tableToPint${i}`}>
                  {tableColumns.map((col) => {
                    const formatter = fieldFormatting.get(col.key);

                    return (
                      <TableCell
                        key={`${transaction.id}-${col.key}`}
                        align='center'>
                        {formatter
                          ? formatter(transaction[col.key])
                          : transaction[col.key] === transaction?.employeeId
                          ? employeesList.find(
                              (employee) =>
                                employee.id === transaction.employeeId,
                            )?.name
                          : transaction[col.key] === transaction?.lineSectionId
                          ? getLineNameByLineSection(transaction?.lineSectionId)
                          : transaction[col.key] || '---'}
                      </TableCell>
                    );
                  })}
                  {transportOperatorSelected.type === 1 && (
                    <TableCell>
                      {transportOperatorsList.find(
                        (e) => e.id === transaction?.transportOperatorId,
                      )?.name || '---'}
                    </TableCell>
                  )}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    );

    return isPrinting ? (
      <TableToPrint />
    ) : (
      <TableVirtuoso
        style={{
          height: '90vh',
          overflowY: 'hidden',
        }}
        overscan={10}
        useWindowScroll
        data={stableSort(
          initialTransactionsList,
          getComparator(order, orderBy),
        )}
        components={{
          Scroller: React.forwardRef((props, ref) => (
            <TableContainer className={classes.table} {...props} ref={ref} />
          )),

          Table: (props: any) => (
            <Table {...props} style={{ borderCollapse: 'separate' }} />
          ),
          TableHead,
          TableRow,
          TableBody: React.forwardRef((props, ref) => (
            <TableBody {...props} ref={ref} />
          )),
        }}
        fixedHeaderContent={() => (
          <TableRow>
            {tableColumns.length > 0 &&
              tableColumns.map((col) => (
                <TableCell
                  key={col.key}
                  variant='head'
                  align='center'
                  sortDirection={orderBy === col.key ? order : false}>
                  <TableSortLabel
                    classes={{
                      root: classes.sortLabel,
                      icon: classes.sortLabel,
                    }}
                    active
                    direction={orderBy === col.key ? order : 'asc'}
                    onClick={(event) => handleRequestSort(event, col.key)}>
                    {col.name}
                    {orderBy === col.key ? (
                      <span className={classes.visuallyHidden}>
                        {order === 'desc'
                          ? 'sorted descending'
                          : 'sorted ascending'}
                      </span>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
              ))}
            {transportOperatorSelected.type === 1 && (
              <TableCell
                variant='head'
                align='left'
                sortDirection={
                  orderBy === 'transportOperatorId' ? order : false
                }>
                <TableSortLabel
                  classes={{
                    root: classes.sortLabel,
                    icon: classes.sortLabel,
                  }}
                  active
                  direction={orderBy === 'transportOperatorId' ? order : 'asc'}
                  onClick={(event) =>
                    handleRequestSort(event, 'transportOperatorId')
                  }>
                  Operação
                  {orderBy === 'transportOperatorId' ? (
                    <span className={classes.visuallyHidden}>
                      {order === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>
            )}
          </TableRow>
        )}
        itemContent={(index: number, transaction: any) => (
          <>
            {tableColumns.map((col) => {
              const formatter = fieldFormatting.get(col.key);
              return (
                <TableCell key={`${transaction.id}-${col.key}`} align='center'>
                  {formatter
                    ? formatter(transaction[col.key])
                    : transaction[col.key] || '---'}
                </TableCell>
              );
            })}
            {transportOperatorSelected.type === 1 && (
              <TableCell>
                {transportOperatorsList.find(
                  (e) => e.id === transaction?.transportOperatorId,
                )?.name || '---'}
              </TableCell>
            )}
          </>
        )}
      />
    );
  },
);

export { TransactionNavigation };
export default TransactionNavigation;
